import { useEffect, useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  Paperclip,
  Upload,
  X,
  BadgePercent,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatDateHu } from "@/components/DeductionFormDialog";
import { Th, Td, openProtectedFile } from "@/components/employee/tabHelpers";

export const EMPLOYEE_BENEFIT_TYPES = [
  {
    value: "mothers_three_plus",
    label: "Három vagy több gyereket nevelő anyák kedvezménye",
  },
  { value: "mothers_two", label: "Két gyerekes anyák SZJA kedvezménye" },
  { value: "under_25", label: "25 év alatti fiatalok kedvezménye" },
  { value: "mothers_under_30", label: "30 év alatti anyák kedvezménye" },
  { value: "personal", label: "Személyi kedvezmény" },
  { value: "first_marriage", label: "Első házasok kedvezménye" },
  { value: "family", label: "Családi kedvezmény" },
];

const labelOf = (v) =>
  EMPLOYEE_BENEFIT_TYPES.find((t) => t.value === v)?.label || v || "—";

const ACCEPTED = ".pdf,application/pdf,image/jpeg,image/png,image/webp,image/heic";
const MAX_BYTES = 20 * 1024 * 1024;

export function BenefitsSection({ employeeId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/benefits`);
      setItems(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a kedvezményeket."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const onCreated = (b) => setItems((prev) => [b, ...prev]);

  const onDelete = async (b) => {
    if (!window.confirm("Biztosan törlöd ezt a kedvezményt?")) return;
    try {
      await api.delete(`/employees/${employeeId}/benefits/${b._id}`);
      setItems((prev) => prev.filter((x) => x._id !== b._id));
      toast.success("Kedvezmény törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  return (
    <section
      data-testid="benefits-section"
      className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]"
    >
      <header className="flex flex-col gap-1 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Adókedvezmények
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {items.length === 0
              ? "Még nincs rögzített kedvezmény."
              : `${items.length} kedvezmény rögzítve`}
          </p>
        </div>
        <Button
          size="sm"
          data-testid="new-benefit-button"
          onClick={() => setOpenCreate(true)}
        >
          <Plus className="h-4 w-4" />
          Új kedvezmény
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="benefits-empty"
          className="flex flex-col items-center gap-3 px-6 py-14 text-center"
        >
          <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
            <BadgePercent className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          </div>
          <p className="text-sm font-medium">Nincs rögzített kedvezmény</p>
          <p className="text-xs text-muted-foreground">
            Add hozzá az első adókedvezményt — típus, érvényesség és opcionális
            igazolás.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2 h-8"
            onClick={() => setOpenCreate(true)}
          >
            <Plus className="h-3.5 w-3.5" /> Új kedvezmény
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="benefits-table">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <Th>Típus</Th>
                <Th>Kezdete</Th>
                <Th>Vége</Th>
                <Th>Igazolás</Th>
                <Th>Rögzítette</Th>
                <Th className="pr-4 text-right">Művelet</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it._id}
                  data-testid={`benefit-row-${it._id}`}
                  className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                >
                  <Td>
                    <span className="font-medium">{labelOf(it.benefit_type)}</span>
                  </Td>
                  <Td className="tabular-nums">{formatDateHu(it.start_date)}</Td>
                  <Td className="tabular-nums text-muted-foreground">
                    {it.end_date ? formatDateHu(it.end_date) : "Határozatlan ideig"}
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    {it.file_url ? (
                      <button
                        type="button"
                        data-testid={`benefit-file-${it._id}`}
                        onClick={() =>
                          openProtectedFile(
                            `/employees/${employeeId}/benefits/${it._id}/file`,
                            it.file_content_type,
                            labelOf(it.benefit_type)
                          )
                        }
                        className="inline-flex items-center gap-1.5 text-[12.5px] hover:underline"
                      >
                        <Paperclip className="h-3.5 w-3.5" strokeWidth={1.75} />
                        {it.file_name || "megnyitás"}
                      </button>
                    ) : (
                      <span className="text-[12px] text-muted-foreground/70">—</span>
                    )}
                  </Td>
                  <Td className="text-muted-foreground">
                    {it.created_by_name || "—"}
                    <span className="ml-1 text-muted-foreground/70">
                      · {formatDateHu(it.created_at)}
                    </span>
                  </Td>
                  <Td className="pr-4 text-right">
                    <button
                      type="button"
                      onClick={() => onDelete(it)}
                      data-testid={`benefit-delete-${it._id}`}
                      aria-label="Törlés"
                      className="ml-auto grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateBenefitDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        employeeId={employeeId}
        onCreated={onCreated}
      />
    </section>
  );
}

function CreateBenefitDialog({ open, onOpenChange, employeeId, onCreated }) {
  const [benefitType, setBenefitType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setBenefitType("");
    setStartDate("");
    setEndDate("");
    setFile(null);
    setErr("");
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!benefitType) return setErr("Típus kiválasztása kötelező.");
    if (!startDate) return setErr("Kezdő dátum kötelező.");
    if (endDate && endDate < startDate)
      return setErr("A befejező dátum nem lehet a kezdő dátum előtt.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("benefit_type", benefitType);
      fd.append("start_date", startDate);
      if (endDate) fd.append("end_date", endDate);
      if (file) fd.append("file", file);
      const { data } = await api.post(
        `/employees/${employeeId}/benefits`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Kedvezmény rögzítve.");
      onCreated(data);
      onOpenChange(false);
    } catch (error) {
      setErr(
        formatApiError(error?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`A fájl mérete meghaladja a maximumot (${MAX_BYTES / (1024 * 1024)} MB).`);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const inputId = `benefit-file-${employeeId}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="benefit-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Új adókedvezmény</DialogTitle>
          <DialogDescription>
            Válassz típust, add meg az érvényesség idejét és tölts fel
            igazolást (opcionális).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Típus *" testId="benefit-type">
            <Select value={benefitType} onValueChange={setBenefitType}>
              <SelectTrigger data-testid="benefit-type-trigger">
                <SelectValue placeholder="Válassz típust…" />
              </SelectTrigger>
              <SelectContent>
                {EMPLOYEE_BENEFIT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Kezdete *" testId="benefit-start">
              <DateInput value={startDate} onChange={setStartDate} />
            </Field>
            <Field label="Vége" testId="benefit-end">
              <DateInput value={endDate} onChange={setEndDate} />
            </Field>
          </div>

          <Field label="Fájl (opcionális)" testId="benefit-file-field">
            <input
              id={inputId}
              type="file"
              accept={ACCEPTED}
              className="hidden"
              onChange={onPick}
              data-testid="benefit-file-input"
            />
            {file ? (
              <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                <FileText
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  strokeWidth={1.75}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                  aria-label="Fájl eltávolítása"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="benefit-file-button"
                onClick={() => document.getElementById(inputId)?.click()}
                className={cn(
                  "flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-3 text-sm text-muted-foreground transition-colors",
                  "hover:border-foreground/30 hover:bg-muted/40 hover:text-foreground"
                )}
              >
                <Upload className="h-4 w-4" strokeWidth={1.75} />
                Fájl kiválasztása
              </button>
            )}
          </Field>

          {err && (
            <div
              data-testid="benefit-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Mégse
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="benefit-submit"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Rögzítés"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
